/**
 * Homeowner (HOM) approval requests for a rental property
 * (GET/POST /api/v2/rental/properties/:property_slug/approvals).
 */

import { format, isValid, parseISO } from "date-fns";

/** Category option from GET /api/v2/rental/approval_categories (`slug` is POST `category_id`). */
export type ApprovalCategory = {
  slug: string;
  name: string;
};

export type RentalPropertyHOApprovalListItem = {
  slug: string;
  status: string;
  resource_type: string;
  resource_id?: number;
  /** Legacy / optional */
  resource_slug?: string;
  /** Present on GET .../opportunities/:slug/approvals when scoped server-side */
  property_slug?: string;
  /** Some payloads nest property for routing to GET .../properties/:slug/approvals/:id */
  property?: { slug?: string };
  /** Property (home) name from the approval decorator default view */
  property_name?: string;
  description: string;
  amount?: string | number;
  category: ApprovalCategory | string;
  created_at: string;
  created_by?: string | { id: string; name: string };
};

export type RentalPropertyHOApprovalsPaginate = {
  total_records: number;
  total_pages: number;
  current_page: number;
};

export type RentalPropertyHOApprovalsListPayload = {
  approvals: RentalPropertyHOApprovalListItem[];
  paginate: RentalPropertyHOApprovalsPaginate;
};

/** Query params for GET .../properties/:slug/approvals & GET .../opportunities/:slug/approvals */
export type RentalPropertyHOApprovalsListQuery = {
  status?: string;
  category_id?: string;
  page?: number;
  per_page?: number;
  /**
   * Opportunity list only: filter to these properties (`property_slugs[]=...` repeated).
   * Omit for all properties on the opportunity.
   */
  property_slugs?: string[];
};

export type RentalPropertyHOApprovalReservation = {
  slug?: string;
  check_in?: string;
  check_out?: string;
  adults?: number;
  children?: number;
  client_type?: string;
};

/** Ticket-shaped summary fields from concierge/tickets path */
export type RentalPropertyHOApprovalTicket = {
  number?: string;
  slug?: string;
  title?: string;
  ops_poc_name?: string;
  /** Path 3: concierge ticket budget expense notes */
  budget_expense_comments?: string | null;
};

/** Actor on approve/reject/comment entries — mirrors created_by shape. */
export type RentalPropertyHOApprovalActor =
  | string
  | { id: string; name: string };

/** Comment thread item when API returns activity/comments history */
export type RentalPropertyHOApprovalCommentEntry = {
  body?: string;
  created_at?: string;
  created_by?: RentalPropertyHOApprovalActor;
};

export type RentalPropertyHOApprovalDetail = {
  resource_type: string;
  resource_slug?: string;
  resource_id?: number;
  reservation?: Partial<RentalPropertyHOApprovalReservation>;
  ticket?: Partial<RentalPropertyHOApprovalTicket>;
  category: ApprovalCategory | string;
  amount?: number | string;
  gst_percentage?: string;
  description: string;
  created_by: RentalPropertyHOApprovalActor;
  created_at: string;
  status: string;
  slug: string;
  approval_description?: string;
  attachments?: { url: string }[];
  /** Legacy single rejection/resolution note */
  comment?: string | null;
  comments?: RentalPropertyHOApprovalCommentEntry[];
  approved_by?: RentalPropertyHOApprovalActor;
  approved_at?: string;
  rejected_by?: RentalPropertyHOApprovalActor;
  rejected_at?: string;
};

/** Normalized UI path for conditional summary sections */
export type RentalPropertyHOApprovalResourcePath =
  | "opportunity"
  | "property"
  | "ticket"
  | "unknown";

export function normalizeHoApprovalResourceType(
  raw: string | undefined | null,
): RentalPropertyHOApprovalResourcePath {
  const s = String(raw ?? "").toLowerCase();
  if (!s) {
    return "unknown";
  }
  if (s.includes("ticket")) {
    return "ticket";
  }
  if (s.includes("opportunity")) {
    return "opportunity";
  }
  if (s.includes("property")) {
    return "property";
  }
  return "unknown";
}

function pickFirstNonEmpty(
  ...candidates: (string | undefined | null)[]
): string {
  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t) {
      return t;
    }
  }
  return "";
}

/**
 * Reference string for the Request Summary header row + copy-to-clipboard (OI_v2 Figma).
 * Ticket → ticket.number; property → resource_slug; opportunity → approval slug.
 */
export function hoApprovalReferenceCopyText(
  detail: RentalPropertyHOApprovalDetail,
  resourcePath: RentalPropertyHOApprovalResourcePath,
  approvalSlugFallback?: string | null,
): string {
  const fb = String(approvalSlugFallback ?? "").trim();
  switch (resourcePath) {
    case "ticket":
      return pickFirstNonEmpty(
        detail.ticket?.number,
        detail.resource_slug,
        detail.slug,
        fb,
      );
    case "opportunity":
      return pickFirstNonEmpty(detail.resource_slug, detail.slug, fb);
    case "property":
      return pickFirstNonEmpty(detail.slug, fb);
    default:
      return pickFirstNonEmpty(detail.slug, detail.resource_slug, fb);
  }
}

/**
 * Rails/API payloads sometimes nest the approval object under a wrapper key.
 */
export function unwrapPropertyHOApprovalDetailPayload(raw: unknown): unknown {
  if (raw == null || typeof raw !== "object") {
    return raw;
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  const r = raw as Record<string, unknown>;
  const nestedKeys = [
    "approval",
    "ho_approval",
    "rental_property_ho_approval",
    "hom_property_approval",
    "property_ho_approval",
    "data",
  ];
  for (const key of nestedKeys) {
    const inner = r[key];
    if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
      return unwrapPropertyHOApprovalDetailPayload(inner);
    }
  }
  return raw;
}

/** Fill missing detail fields from the list row when GET …/approvals/:slug omits them */
export function mergeHoApprovalDetailWithListPreview(
  detail: RentalPropertyHOApprovalDetail,
  preview?: RentalPropertyHOApprovalListItem | null,
): RentalPropertyHOApprovalDetail {
  if (!preview) {
    return detail;
  }
  return {
    ...detail,
    resource_type: detail.resource_type || preview.resource_type || "",
    slug: detail.slug || preview.slug || "",
    status: detail.status || preview.status || "",
    description:
      detail.description != null && detail.description !== ""
        ? detail.description
        : preview.description ?? "",
    category: detail.category ?? preview.category ?? "",
    amount: detail.amount ?? preview.amount,
    created_at: detail.created_at || preview.created_at || "",
    created_by: detail.created_by ?? preview.created_by ?? "",
    resource_slug: detail.resource_slug ?? preview.resource_slug,
    resource_id: detail.resource_id ?? preview.resource_id,
  };
}

function normalizeHoApprovalStatus(status: string | undefined | null): string {
  return String(status ?? "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .trim();
}

/** HOM POC may approve/reject only while still pending */
export function hoApprovalStatusIsPending(
  status: string | undefined | null,
): boolean {
  const n = normalizeHoApprovalStatus(status);
  return n === "" || n === "pending";
}

export function hoApprovalStatusIsApproved(
  status: string | undefined | null,
): boolean {
  return normalizeHoApprovalStatus(status) === "approved";
}

export function hoApprovalStatusIsRejected(
  status: string | undefined | null,
): boolean {
  return normalizeHoApprovalStatus(status) === "rejected";
}

export function formatHoApprovalCurrency(
  amount: number | string | undefined | null,
): string {
  if (amount === undefined || amount === null || amount === "") {
    return "—";
  }
  const n = Number(amount);
  if (Number.isNaN(n) || n === 0) {
    return "—";
  }
  return `₹${n.toLocaleString("en-IN")}`;
}

export function formatHoApprovalDateTime(
  iso: string | undefined | null,
): string {
  if (iso == null || iso === "") {
    return "—";
  }
  const d = parseISO(iso);
  const date = isValid(d) ? d : new Date(iso);
  if (!isValid(date)) {
    return iso;
  }
  return format(date, "d MMM yyyy, h:mm a");
}

export type RentalPropertyHOApprovalListItemWithProperty =
  RentalPropertyHOApprovalListItem & {
    property_slug: string;
  };

/** Property slug for opening HO approval detail (properties/:property_slug/approvals/:slug). */
export function hoApprovalListItemPropertySlug(
  item: RentalPropertyHOApprovalListItem,
): string {
  if (item.property_slug) {
    return item.property_slug;
  }
  const nested = item.property?.slug;
  if (nested) {
    return nested;
  }
  const rt = String(item.resource_type || "").toLowerCase();
  if (
    (rt.includes("property") || rt.includes("rental::property")) &&
    item.resource_slug
  ) {
    return item.resource_slug;
  }
  return "";
}

/** Display label for list/detail category field (API object or legacy string). */
export function hoApprovalCategoryLabel(
  category: ApprovalCategory | string | undefined | null,
): string {
  if (category == null) {
    return "";
  }
  if (typeof category === "object" && "name" in category) {
    return category.name;
  }
  return typeof category === "string" ? category : "";
}

/** Display name for created_by on detail views. */
export function hoApprovalCreatedByLabel(
  createdBy: RentalPropertyHOApprovalDetail["created_by"] | undefined | null,
): string {
  if (createdBy == null) {
    return "";
  }
  if (
    typeof createdBy === "object" &&
    createdBy !== null &&
    "name" in createdBy
  ) {
    return createdBy.name;
  }
  return typeof createdBy === "string" ? createdBy : "";
}
